/**
 * Yumz share-cards v2 — Flow A renderer ("I'm here now" / check-in).
 *
 * Two layouts selected by photo availability:
 *
 *  - No photo: graphic check-in. Place name is the hero typography.
 *    Atmospheric background per status (see drawStatusAtmosphere).
 *    Canvas 1080×1350 — fastest renderer, target <1.2s on iPhone.
 *
 *  - With photo: sticker check-in. Polaroid mat hero containing the user's
 *    photo (friends / table / interior — never a dish). Place name in
 *    display weight below. Status as tilted sticker. Canvas 1080×1920.
 *
 * The status describes the PLACE/VIBE in Flow A, never the dish.
 */

import type { ShareStatus } from "./shareStatus";
import { SHARE_STATUSES, pickStatusCopy } from "./shareStatus";
import type { PreloadedImages } from "./shareCardHelpers";
import {
  loadImage,
  drawStatusAtmosphere,
  drawStatusPill,
  drawPolaroidMat,
  drawYumzStamp,
  drawGrain,
  drawWrappedText,
  fillRoundedRect,
  strokeRoundedRect,
  canvasToBlob,
  SYSTEM_DISPLAY,
  SYSTEM_BODY,
  SYSTEM_HANDSCRIPT,
} from "./shareCardHelpers";

export interface CheckInCardParams {
  restaurantName: string;
  locationLabel?: string;
  status: ShareStatus;
  photoUrl?: string;
  photoCaption?: string;
  timeLabel?: string;
  verdictLine?: string;
  preloaded?: PreloadedImages;
  quality?: number;
}

export async function renderCheckIn(params: CheckInCardParams): Promise<Blob> {
  const {
    restaurantName,
    locationLabel,
    status,
    photoUrl,
    photoCaption,
    timeLabel,
    verdictLine,
    preloaded,
    quality,
  } = params;

  const def = SHARE_STATUSES[status];
  const hasPhoto = !!photoUrl;
  const W = 1080;
  const H = hasPhoto ? 1920 : 1350;

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("renderCheckIn: 2D context unavailable");
  }

  drawStatusAtmosphere(ctx, status, W, H);

  const imgCache = new Map<string, Promise<HTMLImageElement>>();

  if (hasPhoto && photoUrl) {
    try {
      const photo = await loadImage(photoUrl, imgCache, preloaded);
      const matW = Math.round(W * 0.78);
      const matH = Math.round(matW * 1.18);
      const matCx = W / 2;
      const matCy = Math.round(H * 0.42);
      drawPolaroidMat(ctx, photo, matCx, matCy, matW, matH, -4, photoCaption);
    } catch {
      // Photo failed — fall through to all-typography layout.
    }

    ctx.save();
    ctx.translate(W - 140, 200);
    ctx.rotate((8 * Math.PI) / 180);
    drawStatusPill(ctx, status, 0, 0, 36, { align: "center" });
    ctx.restore();

    if (locationLabel) {
      ctx.save();
      ctx.translate(180, Math.round(H * 0.66));
      ctx.rotate((-6 * Math.PI) / 180);
      drawLocationTape(ctx, locationLabel, def.palette.accent, def.palette.accentText);
      ctx.restore();
    }

    const scribbleHeader = pickStatusCopy(status, "checkInHeaders");
    if (scribbleHeader) {
      ctx.save();
      ctx.translate(90, 230);
      ctx.rotate((-10 * Math.PI) / 180);
      ctx.fillStyle = withAlpha(def.palette.text, 0.65);
      ctx.font = `500 56px ${SYSTEM_HANDSCRIPT}`;
      ctx.textAlign = "left";
      ctx.fillText(scribbleHeader, 0, 0);
      ctx.restore();
    }

    const footerY = Math.round(H * 0.79);
    ctx.fillStyle = def.palette.text;
    ctx.font = `900 78px ${SYSTEM_DISPLAY}`;
    ctx.textAlign = "center";
    ctx.fillText(restaurantName.toUpperCase(), W / 2, footerY);

    const vline = verdictLine ?? pickStatusCopy(status, "checkInLines");
    if (vline) {
      ctx.fillStyle = withAlpha(def.palette.text, 0.85);
      ctx.font = `italic 500 42px ${SYSTEM_HANDSCRIPT}`;
      drawWrappedText(ctx, `"${vline}"`, W / 2, footerY + 76, W - 160, 54, "center");
    }
  } else {
    const header = pickStatusCopy(status, "checkInHeaders") || "currently at";
    ctx.fillStyle = withAlpha(def.palette.text, 0.78);
    ctx.font = `800 32px ${SYSTEM_BODY}`;
    ctx.textAlign = "left";
    ctx.fillText(letterSpace(header.toUpperCase()), 64, 100);

    drawStatusPill(ctx, status, W - 64, 100, 32, { align: "right" });

    const placeText = restaurantName.toUpperCase();
    const words = placeText.split(/\s+/);
    const lineCount = words.length;
    // Initial font size by line count — what fits cleanly for typical names.
    let baseFontPx = lineCount <= 2 ? 220 : lineCount <= 3 ? 180 : 140;
    // Shrink font until the widest word fits inside the safe horizontal area
    // (canvas width minus the 64px left gutter and a small right margin).
    // Fixes long words like STEAKHOUSE / RESTAURANT that overflow at 220px.
    const safeTextWidth = W - 64 - 32;
    {
      const measureFont = (px: number) => `900 ${px}px ${SYSTEM_DISPLAY}`;
      ctx.font = measureFont(baseFontPx);
      let widest = 0;
      for (const w of words) {
        const m = ctx.measureText(w).width;
        if (m > widest) widest = m;
      }
      if (widest > safeTextWidth) {
        baseFontPx = Math.max(72, Math.floor(baseFontPx * (safeTextWidth / widest)));
      }
    }
    const lineH = Math.round(baseFontPx * 0.92);

    const useGoldText = status === "elite";
    if (useGoldText) {
      const grad = ctx.createLinearGradient(0, 0, 0, lineCount * lineH);
      grad.addColorStop(0, "#fff4cc");
      grad.addColorStop(0.6, "#d4af37");
      grad.addColorStop(1, "#8a6310");
      ctx.fillStyle = grad;
    } else {
      ctx.fillStyle = def.palette.text;
    }
    ctx.font = `900 ${baseFontPx}px ${SYSTEM_DISPLAY}`;
    ctx.textAlign = "left";
    const stackBottom = Math.round(H * 0.62);
    const startY = stackBottom - (lineCount - 1) * lineH;
    for (let i = 0; i < lineCount; i++) {
      ctx.fillText(words[i], 64, startY + i * lineH);
    }

    const chipY = stackBottom + Math.round(baseFontPx * 0.3) + 28;
    let chipX = 64;
    if (locationLabel) {
      chipX += drawChip(ctx, locationLabel, chipX, chipY, def) + 10;
    }
    if (timeLabel) {
      drawChip(ctx, timeLabel, chipX, chipY, def);
    }

    const vline = verdictLine ?? pickStatusCopy(status, "checkInLines");
    if (vline) {
      ctx.fillStyle = withAlpha(def.palette.text, 0.82);
      ctx.font = `500 34px ${SYSTEM_BODY}`;
      drawWrappedText(ctx, vline, 64, chipY + 80, W - 128, 46, "left");
    }

    if (status === "honest") drawHonestStamp(ctx, W, H);
    else if (status === "elite") drawEliteSeal(ctx, W, H);
    else if (status === "cozy") drawCandleFlame(ctx, W, H);
  }

  const grainAlpha = def.texture === "candle" || def.texture === "paper" ? 0.14 : 0.08;
  drawGrain(ctx, W, H, 0.4, grainAlpha);
  drawYumzStamp(ctx, status, W, H);

  const q = quality ?? (hasPhoto ? 0.88 : 0.84);
  return canvasToBlob(canvas, q);
}

// ─── local helpers ──────────────────────────────────────────────────

function drawChip(
  ctx: CanvasRenderingContext2D,
  label: string,
  x: number,
  y: number,
  def: typeof SHARE_STATUSES[ShareStatus],
): number {
  ctx.save();
  ctx.font = `700 26px ${SYSTEM_BODY}`;
  const text = label.toUpperCase();
  const textW = ctx.measureText(text).width;
  const padX = 22;
  const padY = 14;
  const w = textW + padX * 2;
  const h = 26 + padY * 2;
  const fill = def.texture === "paper" ? "rgba(26,18,7,0.08)" : "rgba(255,255,255,0.14)";
  const stroke = def.texture === "paper" ? "rgba(26,18,7,0.18)" : "rgba(255,255,255,0.22)";
  fillRoundedRect(ctx, x, y - h / 2, w, h, h / 2, fill);
  strokeRoundedRect(ctx, x, y - h / 2, w, h, h / 2, stroke, 1.5);
  ctx.fillStyle = def.texture === "paper" ? "#3a2814" : "rgba(255,255,255,0.92)";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(text, x + padX, y);
  ctx.restore();
  return w;
}

function drawLocationTape(
  ctx: CanvasRenderingContext2D,
  label: string,
  bg: string,
  fg: string,
): void {
  ctx.save();
  ctx.font = `800 26px ${SYSTEM_BODY}`;
  const text = label.toUpperCase();
  const textW = ctx.measureText(text).width;
  const padX = 28;
  const padY = 14;
  const w = textW + padX * 2;
  const h = 26 + padY * 2;
  ctx.shadowColor = "rgba(0,0,0,0.4)";
  ctx.shadowBlur = 16;
  ctx.shadowOffsetY = 6;
  fillRoundedRect(ctx, -w / 2, -h / 2, w, h, 6, bg);
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.fillStyle = fg;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, 0, 0);
  ctx.restore();
}

function drawHonestStamp(ctx: CanvasRenderingContext2D, W: number, _H: number): void {
  ctx.save();
  ctx.translate(120, 240);
  ctx.rotate((-3 * Math.PI) / 180);
  ctx.strokeStyle = "#d23300";
  ctx.lineWidth = 4;
  ctx.fillStyle = "#d23300";
  ctx.font = `800 32px ${SYSTEM_BODY}`;
  const label = "LIVE TAKE";
  const tw = ctx.measureText(label).width;
  ctx.strokeRect(-tw / 2 - 18, -28, tw + 36, 56);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, 0, 0);
  ctx.restore();

  ctx.save();
  ctx.translate(W - 180, 660);
  ctx.rotate((-8 * Math.PI) / 180);
  ctx.fillStyle = "#d23300";
  ctx.font = `500 68px ${SYSTEM_HANDSCRIPT}`;
  ctx.textAlign = "center";
  ctx.fillText("walked in →", 0, 0);
  ctx.restore();
}

function drawEliteSeal(ctx: CanvasRenderingContext2D, W: number, _H: number): void {
  ctx.save();
  ctx.translate(W - 160, 220);
  ctx.rotate((-6 * Math.PI) / 180);
  ctx.strokeStyle = "#d4af37";
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.arc(0, 0, 110, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = "#d4af37";
  ctx.font = `800 18px ${SYSTEM_BODY}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("YUMZ", 0, -28);
  ctx.fillText("VERIFIED", 0, 0);
  ctx.fillText("ELITE", 0, 28);
  ctx.restore();
}

function drawCandleFlame(ctx: CanvasRenderingContext2D, W: number, _H: number): void {
  ctx.save();
  const cx = W / 2;
  const g = ctx.createRadialGradient(cx, 30, 0, cx, 30, 200);
  g.addColorStop(0, "rgba(255,220,160,0.85)");
  g.addColorStop(0.5, "rgba(255,180,80,0.35)");
  g.addColorStop(1, "rgba(255,180,80,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, 280);
  ctx.restore();
}

function letterSpace(text: string): string {
  return text.split("").join(" ");
}

function withAlpha(color: string, alpha: number): string {
  if (/^#[0-9a-f]{6}$/i.test(color)) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  return color;
}
